/**
 * FilterBar — application and environment filter controls (BR-U5-13 through BR-U5-16).
 *
 * Renders two <select> dropdowns that let the user filter the dashboard view by
 * Application and/or Environment. Selecting "All" in either dropdown clears that
 * filter (sets it to null in StackFilter).
 *
 * Available options are derived from the full stacks list so no extra API call
 * is needed (BR-U5-14). Options are sorted alphabetically (BR-U5-15).
 */
import type { StackFilter } from '@/types';
import styles from './FilterBar.module.css';

export interface FilterBarProps {
  filters: StackFilter;
  onFilterChange: (f: StackFilter) => void;
  /** All unique application names available for filtering */
  applications: string[];
  /** All unique environment names available for filtering */
  environments: string[];
}

export function FilterBar({
  filters,
  onFilterChange,
  applications,
  environments,
}: FilterBarProps) {
  const handleApplicationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({
      ...filters,
      application: e.target.value === '' ? null : e.target.value,
    });
  };

  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({
      ...filters,
      environment: e.target.value === '' ? null : e.target.value,
    });
  };

  const handleClear = () => {
    onFilterChange({ application: null, environment: null });
  };

  const isFiltered = filters.application !== null || filters.environment !== null;

  return (
    <div className={styles.container} role="search" aria-label="Filter stacks">
      <div className={styles.group}>
        <label htmlFor="filter-application" className={styles.label}>
          Application
        </label>
        <select
          id="filter-application"
          className={styles.select}
          value={filters.application ?? ''}
          onChange={handleApplicationChange}
        >
          <option value="">All applications</option>
          {applications.map((app) => (
            <option key={app} value={app}>
              {app}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.group}>
        <label htmlFor="filter-environment" className={styles.label}>
          Environment
        </label>
        <select
          id="filter-environment"
          className={styles.select}
          value={filters.environment ?? ''}
          onChange={handleEnvironmentChange}
        >
          <option value="">All environments</option>
          {environments.map((env) => (
            <option key={env} value={env}>
              {env}
            </option>
          ))}
        </select>
      </div>

      {isFiltered && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClear}
          aria-label="Clear all filters"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
